import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { documentWorkflowControllerBulkRemindSignersStatusOptions } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import type { BulkRemindSignersJobStatusRes } from '@dealstack/shared/lib/backendClient';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { BULK_REMIND_SIGNERS_TERMINAL_STATUSES } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/BulkRemindSignersModal.types';

const POLL_INTERVAL_MS = 2000;

export const useBulkRemindSignersStatus = ({
	dealId,
	programmeId,
	jobId,
	onTerminal,
}: {
	dealId: string;
	programmeId: string;
	jobId: string | null;
	onTerminal?: (state: BulkRemindSignersJobStatusRes) => void;
}) => {
	const queryClient = useQueryClient();
	const documentListKey = formatUnicorn(
		API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_LIST_GET,
		{ dealId, programmeId }
	);

	const q = useQuery({
		...documentWorkflowControllerBulkRemindSignersStatusOptions({
			path: { dealId, programmeId, jobId: jobId ?? '' },
		}),
		enabled: !!jobId,
		refetchInterval: (query) => {
			const data = query.state.data;
			if (!data) {
				return POLL_INTERVAL_MS;
			}
			if (BULK_REMIND_SIGNERS_TERMINAL_STATUSES.includes(data.status)) {
				return false;
			}
			return POLL_INTERVAL_MS;
		},
	});

	useEffect(() => {
		if (!q.isSuccess) {
			return;
		}
		void (async () => {
			const data = q.data;
			if (BULK_REMIND_SIGNERS_TERMINAL_STATUSES.includes(data.status)) {
				await queryClient.invalidateQueries({ queryKey: [documentListKey] });
				onTerminal?.(data);
			}
		})();
	}, [q.isSuccess, q.data]);

	return q;
};
